/**
 * @file 批量轮询管理器
 * @description 将多个任务合并成一次批量请求，按 key 分发结果。
 */

/**
 * 默认轮询间隔（ms）
 */
const DEFAULT_INTERVAL = 3000;

/**
 * 默认单任务最大连续错误次数
 */
const DEFAULT_MAX_RETRIES = 3;

export interface TaskCallbacks<T> {
    /**
     * 根据返回数据判断是否继续轮询该任务
     */
    shouldContinue: (data: T) => boolean;

    /**
     * 每次轮询拿到数据的回调
     */
    onProgress?: (data: T) => void;

    /**
     * 任务自然结束时的回调（shouldContinue 返回 false）
     */
    onSettled?: (data: T) => void;

    /**
     * 该任务出错时的回调
     */
    onError?: (error: Error) => void;
}

export interface PollingManagerOptions<T> {
    /**
     * 批量查询函数，接收活跃的 key 列表，返回每个 key 对应的结果
     */
    queryFn: (keys: string[]) => Promise<Record<string, T>>;

    /**
     * 轮询间隔 ms，默认 3000
     */
    interval?: number;

    /**
     * 单个任务连续错误最大次数，超过后移除该任务，默认 3
     */
    maxRetries?: number;
}

/**
 * 内部存储结构：任务回调 + 连续错误计数
 */
interface TaskEntry<T> {
    callbacks: TaskCallbacks<T>;
    errorCount: number;
}

export class PollingManager<T> {
    private readonly tasks = new Map<string, TaskEntry<T>>();
    private readonly queryFn: PollingManagerOptions<T>['queryFn'];
    private readonly interval: number;
    private readonly maxRetries: number;

    private timer: ReturnType<typeof setTimeout> | null = null;
    private polling = false;
    private destroyed = false;

    constructor(options: PollingManagerOptions<T>) {
        this.queryFn = options.queryFn;
        this.interval = options.interval ?? DEFAULT_INTERVAL;
        this.maxRetries = options.maxRetries ?? DEFAULT_MAX_RETRIES;
    }

    /** 当前活跃任务数 */
    get size() {
        return this.tasks.size;
    }

    /** 是否存在某个任务 */
    has(key: string) {
        return this.tasks.has(key);
    }

    /**
     * 注册一个轮询任务（首个任务加入时自动启动轮询）
     */
    add(key: string, callbacks: TaskCallbacks<T>) {
        this.tasks.set(key, {callbacks, errorCount: 0});
        this.ensurePolling();
    }

    /**
     * 移除一个轮询任务（无剩余任务时自动停止轮询）
     */
    remove(key: string) {
        this.tasks.delete(key);
        if (this.tasks.size === 0) {
            this.stopTimer();
        }
    }

    /**
     * 销毁 Manager，清理所有任务和 timer
     */
    destroy() {
        this.destroyed = true;
        this.tasks.clear();
        this.stopTimer();
    }

    private ensurePolling() {
        if (this.destroyed || this.polling || this.timer) {
            return;
        }
        void this.poll();
    }

    private stopTimer() {
        if (this.timer) {
            clearTimeout(this.timer);
            this.timer = null;
        }
    }

    private scheduleNext() {
        if (this.destroyed || this.tasks.size === 0) {
            return;
        }
        this.timer = setTimeout(() => {
            this.timer = null;
            void this.poll();
        }, this.interval);
    }

    private async poll() {
        if (this.destroyed || this.tasks.size === 0) {
            return;
        }

        this.polling = true;
        const activeKeys = [...this.tasks.keys()];

        try {
            const results = await this.queryFn(activeKeys);
            this.handleSuccess(activeKeys, results);
        } catch (err) {
            const error = err instanceof Error ? err : new Error(String(err));
            this.handleError(activeKeys, error);
        } finally {
            this.polling = false;
            this.scheduleNext();
        }
    }

    /**
     * 处理成功分支：分发数据，清零错误计数，自然结束任务移除
     */
    private handleSuccess(activeKeys: string[], results: Record<string, T>) {
        const toRemove: string[] = [];

        for (const key of activeKeys) {
            const entry = this.tasks.get(key);
            if (!entry || !Object.hasOwn(results, key)) {
                continue;
            }

            const data = results[key];
            entry.errorCount = 0;
            entry.callbacks.onProgress?.(data);

            if (!entry.callbacks.shouldContinue(data)) {
                entry.callbacks.onSettled?.(data);
                toRemove.push(key);
            }
        }

        this.removeBatch(toRemove);
    }

    /**
     * 处理错误分支：累计错误，仅在超过 maxRetries 后回调 onError 并移除任务，
     * 中间的瞬时失败（网络抖动等）静默重试。
     */
    private handleError(activeKeys: string[], error: Error) {
        const toRemove: string[] = [];

        for (const key of activeKeys) {
            const entry = this.tasks.get(key);
            if (!entry) {
                continue;
            }

            entry.errorCount += 1;

            if (entry.errorCount >= this.maxRetries) {
                entry.callbacks.onError?.(error);
                toRemove.push(key);
            }
        }

        this.removeBatch(toRemove);
    }

    private removeBatch(keys: string[]) {
        for (const key of keys) {
            this.remove(key);
        }
    }
}
